import {Menu} from '@albert-io/atomic';
import type {Placement} from '@popperjs/core';
import React from 'react';

export const AUDIO_PLAYBACK_RATES = [0.5, 1, 1.25, 1.5, 2];

// e.g. 0.5x, 1x, 1.25x, 1.5x, 2x
export const formatPlaybackRate = (rate: number) => `${rate}x`;

const stopPropagation = (e: React.SyntheticEvent) => e.stopPropagation();

interface Props {
  playbackRate: number;
  onSelectRate: (rate: number) => void;
  /** Menu.Trigger render prop — spread the received props onto the variant's own speed button. */
  renderTrigger: React.ComponentProps<typeof Menu.Trigger>['children'];
  position?: Placement;
  positionFixed?: boolean;
}

/**
 * Shared playback-speed dropdown for both audio player variants (the small supplement chip and the
 * full AudioPlayer), so their speed interaction and options stay in sync. Lists every speed
 * (0.5x–2x) and reports the selected rate. The menu fits its content rather than the default 240px,
 * and each option is at least 44px wide.
 */
export const PlaybackRateMenu = ({
  playbackRate,
  onSelectRate,
  renderTrigger,
  position = 'bottom-start',
  positionFixed
}: Props) => (
  <Menu position={position} positionFixed={positionFixed} closeOnItemClick>
    <Menu.Trigger>{renderTrigger}</Menu.Trigger>
    <Menu.Content className='[&>.o-menu-content]:min-w-[44px]!'>
      {AUDIO_PLAYBACK_RATES.map((rate) => (
        <Menu.Item
          key={rate}
          className='min-w-[44px] cursor-pointer! text-left **:cursor-pointer!'
          selected={rate === playbackRate}
          onPointerDown={stopPropagation}
          onClick={(e) => {
            // Don't let the click reach a surrounding draggable chip (small variant).
            stopPropagation(e);
            onSelectRate(rate);
          }}
        >
          {formatPlaybackRate(rate)}
        </Menu.Item>
      ))}
    </Menu.Content>
  </Menu>
);

export default PlaybackRateMenu;
